import { useRoute } from 'vue-router';
import PtMessage from '@ui/message';
import store from '@store';
import md5 from 'js-md5';
import accountApis from '@apis/config/graphql/account';
import { awaitWrap } from '@utils/common.util';
import { resetRouter } from '@router/router';
import { useI18n } from 'vue-i18n';
import i18n from '@/assets/i18n';
import { clearLocalUserInfo } from '@master/utils/account.util';

type LogoutType =
    | 'ip_no_access'
    | 'error_token'
    | 'accountInfo_failed'
    | 'no_token'
    | 'user_logout'
    | 'password_change';

export interface Variables {
    where: {
        email: string;
        adminEmail?: string;
        password: string;
        beFromGoogle?: boolean;
        beFromShopify?: boolean;
        token?: string;
    };
    data?: {
        remeberMe: boolean;
        routeQuery: object;
    };
}

export interface Response {
    id: string;
    ptiUid?: string;
    firstName: String;
    name: String;
    email: String;
    password: String;
    phone: String;
    industry: String;
    company: String;
    position: String;
    websiteType: String;
    token: String;
    hasActivated: boolean | null;
    hasAllow: boolean | null;
    createTime: number;
    activatedTime: number;
    updateTime: number;
    signupSource: 'ptengine' | 'ptx'; //2021年03月29日 又加了一个新官网的字段
    permissionsDict?: any;
    permissions?: any;
    i18n: 'EN' | 'JP' | 'ZH';
    status: 0 | 1 | 2;
    freeTrialLong: number;
    accountType: String;
    department: String;
    companySize: String;
    address: String;
    onlyPti: Boolean;
    jobTitle: String;
    loginUtm: String;
    needSecondVerification?: boolean;
    beFirstLogin: boolean;
    isProductIntroPopup?: boolean;
    needGuide: Boolean;
    ptiToken?: string;
    ptiPwd?: string;
    role: String;
    area: 'EN' | 'JP';
    useType: number;
    beFromGoogle: number;
}

export function useLogin() {
    const route = useRoute();
    const { t } = useI18n();
    return {
        t,
        login: async ({
            email,
            password,
            rememberMe,
            beFromGoogle = false,
            beFromShopify = false,
            googleUserToken = ''
        }: {
            email: string;
            password: string;
            rememberMe: boolean;
            beFromGoogle?: boolean;
            beFromShopify?: boolean;
            googleUserToken?: string;
        }) => {
            return await accountApis.login<Response, Variables>({
                where: {
                    email,
                    password: md5(password),
                    beFromGoogle,
                    beFromShopify,
                    token: googleUserToken
                },
                data: { remeberMe: rememberMe, routeQuery: route.query }
            });
        },

        superLogin: async ({
            email,
            adminEmail,
            password,
            rememberMe
        }: {
            email: string;
            adminEmail: string;
            password: string;
            rememberMe: boolean;
        }) => {
            return await accountApis.superLogin<Response, Variables>({
                where: { email, adminEmail, password: md5(password) }
            });
        },

        verifyLoginCode: async (code: string) => {
            const [err, response] = await awaitWrap(
                accountApis.verifyLoginCode({
                    code
                })
            );
            return err ? Promise.reject(err) : Promise.resolve(response);
        },
        logout
    };
}

export async function activeAccount(email: string) {
    const [err, response] = await awaitWrap(
        accountApis.sendActiveEmail({
            where: {
                email
            }
        })
    );
    return err ? Promise.reject(err) : Promise.resolve(response);
}

/**
 * logout
 */
export const logout = (type: LogoutType, source: string) => {
    store.commit('master/main/SET_ACCOUNT_INFO', null);

    if (type === 'error_token') PtMessage.info(i18n.global.t('login.session_has_expired'));
    if (import.meta.env.MODE !== 'production' && type === 'accountInfo_failed')
        PtMessage.warning('获取账号信息失败，请重新登录。');
    clearLocalUserInfo('logout');

    if (localStorage.getItem('ptDebug')) {
        alert(`logout source: ${source}`);
    }

    // Avoid cookie not cleared
    setTimeout(resetRouter, 100);
    setTimeout(() => {
        location.href = import.meta.env.VITE_BASE_URL + 'login';
    }, 333);
};
